import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api';
import { PageHeader, Skeleton } from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';

interface UsageRow {
  userId: string;
  email: string | null;
  model: string | null;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  costMicrounits: number;
  costUsd: number;
}
interface UsageReport {
  currency: 'USD';
  from: string;
  to: string;
  totals: Omit<UsageRow, 'userId' | 'email' | 'model'>;
  limits: Array<{ scope: string; limitMicrounits: number; period: string }>;
  users: UsageRow[];
}
interface AdminOverview {
  generatedAt: string;
  period: { days: number; from: string; to: string; timezone: string };
  users: {
    verified: number;
    newInPeriod: number;
    activated: number;
    activationRate: number;
    recordedTransactionsInPeriod: number;
  };
  transactions: { totalInPeriod: number; today: number };
  assistant: {
    queries: number;
    uniqueUsers: number;
    failures: number;
    costMicrounits: number;
    costUsd: number;
    activeTrials: number;
    activePlus: number;
  };
  activity: Array<{ date: string; transactions: number; users: number; assistantQueries: number }>;
}

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 4,
});
const integer = new Intl.NumberFormat('es-MX');
const percent = new Intl.NumberFormat('es-MX', { style: 'percent', maximumFractionDigits: 0 });

export function AdminAiUsagePage() {
  const [days, setDays] = useState<7 | 30>(30);
  const overview = useQuery({
    queryKey: ['admin', 'overview', days],
    queryFn: async () =>
      (await api.get<AdminOverview>('/admin/overview', { params: { days } })).data,
  });
  const usage = useQuery({
    queryKey: ['admin', 'ai-usage'],
    queryFn: async () => (await api.get<UsageReport>('/assistant/admin/usage')).data,
  });
  const report = usage.data;
  const dashboard = overview.data;
  const forbidden = [overview.error, usage.error].some(
    (error) => error instanceof AxiosError && error.response?.status === 403,
  );

  const download = () => {
    if (!report) return;
    const rows = [
      'email,userId,model,requests,inputTokens,outputTokens,costUsd',
      ...report.users.map((user) =>
        [
          user.email ?? '',
          user.userId,
          user.model ?? '',
          user.requests,
          user.inputTokens,
          user.outputTokens,
          user.costUsd.toFixed(6),
        ]
          .map(csvCell)
          .join(','),
      ),
    ];
    const url = URL.createObjectURL(
      new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'uso-ia-usuarios.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderPage = (content: ReactNode) => (
    <section className="reference-page w-full">
      <PageHeader
        title="Administración"
        description="Actividad agregada · sin correos, nombres, importes ni contenido de consultas."
        actions={
          forbidden ? undefined : (
            <div className="flex items-center gap-2">
              <div
                className="inline-flex rounded-xl border border-slate-200 bg-white p-1"
                aria-label="Periodo de métricas"
              >
                <PeriodButton active={days === 7} onClick={() => setDays(7)}>
                  7 días
                </PeriodButton>
                <PeriodButton active={days === 30} onClick={() => setDays(30)}>
                  30 días
                </PeriodButton>
              </div>
              <button
                type="button"
                onClick={download}
                disabled={!report}
                className="tap-target rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                Descargar CSV de IA
              </button>
            </div>
          )
        }
      />
      <div className="space-y-6">{content}</div>
    </section>
  );

  if (forbidden) return renderPage(<RestrictedNotice />);
  if (overview.isLoading) return renderPage(<AdminOverviewSkeleton />);
  if (!dashboard)
    return renderPage(
      <p role="alert" className="text-sm text-red-700">
        No pudimos cargar las métricas de administración.
      </p>,
    );

  return renderPage(
    <>
      <section aria-labelledby="product-health">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="product-health" className="text-sm font-bold text-slate-900">
            Salud de producto
          </h2>
          <p className="text-xs text-slate-500">
            Últimos {dashboard.period.days} días · {dashboard.period.timezone}
          </p>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Metric
            label="Usuarios verificados"
            value={integer.format(dashboard.users.verified)}
            detail="Cuentas con correo validado"
          />
          <Metric
            label="Nuevos usuarios"
            value={integer.format(dashboard.users.newInPeriod)}
            detail={`En ${dashboard.period.days} días`}
          />
          <Metric
            label="Registraron movimientos"
            value={integer.format(dashboard.users.recordedTransactionsInPeriod)}
            detail="Usuarios distintos en el periodo"
          />
          <Metric
            label="Movimientos hoy"
            value={integer.format(dashboard.transactions.today)}
            detail={`${integer.format(dashboard.transactions.totalInPeriod)} en el periodo`}
          />
          <Metric
            label="Activación"
            value={percent.format(dashboard.users.activationRate)}
            detail={`${integer.format(dashboard.users.activated)} usuarios con al menos un movimiento`}
          />
        </div>
      </section>

      <section
        className="rounded-2xl border border-slate-200 bg-white p-5"
        aria-labelledby="daily-activity"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h2 id="daily-activity" className="font-semibold text-slate-900">
              Registros diarios
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Actividad medida por el momento en que se registró un movimiento, no por su fecha
              financiera.
            </p>
          </div>
          <span className="text-xs text-slate-500">Zona horaria: Ciudad de México</span>
        </div>
        <div
          className="mt-5 h-64"
          role="img"
          aria-label="Gráfica diaria de movimientos y usuarios que registraron movimientos"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dashboard.activity} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                labelFormatter={(value) => fullDate(String(value))}
                formatter={(value: number, name: string) => [
                  integer.format(value),
                  name === 'transactions' ? 'Movimientos' : 'Usuarios',
                ]}
              />
              <Bar
                dataKey="transactions"
                name="transactions"
                fill="#0f172a"
                radius={[5, 5, 0, 0]}
              />
              <Bar dataKey="users" name="users" fill="#047857" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section
        className="rounded-2xl border border-slate-200 bg-white p-5"
        aria-labelledby="assistant-health"
      >
        <div>
          <h2 id="assistant-health" className="font-semibold text-slate-900">
            Asistente e IA
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Operaciones, costo y acceso durante el periodo seleccionado.
          </p>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Metric
            label="Consultas exitosas"
            value={integer.format(dashboard.assistant.queries)}
            detail={`${integer.format(dashboard.assistant.uniqueUsers)} usuarios distintos`}
            compact
          />
          <Metric
            label="Costo estimado"
            value={money.format(dashboard.assistant.costUsd)}
            detail="Sólo operaciones exitosas"
            compact
          />
          <Metric
            label="Fallos"
            value={integer.format(dashboard.assistant.failures)}
            detail="Respuestas no completadas"
            compact
          />
          <Metric
            label="Pruebas vigentes"
            value={integer.format(dashboard.assistant.activeTrials)}
            detail="Créditos de prueba no vencidos"
            compact
          />
          <Metric
            label="Plus activo"
            value={integer.format(dashboard.assistant.activePlus)}
            detail="Entitlements con asistente"
            compact
          />
        </div>
      </section>

      <AiUsageDetails report={report} loading={usage.isLoading} />
    </>,
  );
}

function AiUsageDetails({
  report,
  loading,
}: {
  report: UsageReport | undefined;
  loading: boolean;
}) {
  if (loading) return <AdminUsageSkeleton />;
  if (!report)
    return (
      <p role="alert" className="text-sm text-red-700">
        No pudimos cargar el detalle de IA.
      </p>
    );
  return (
    <section className="rounded-2xl border border-slate-200 bg-white" aria-labelledby="ai-detail">
      <header className="border-b border-slate-100 px-5 py-4">
        <h2 id="ai-detail" className="font-semibold text-slate-900">
          Detalle de uso de IA
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Últimos 30 días · correos visibles sólo para administración; sin prompts ni respuestas.
        </p>
      </header>
      <div className="grid gap-3 border-b border-slate-100 p-5 sm:grid-cols-3">
        <Metric label="Costo acumulado" value={money.format(report.totals.costUsd)} compact />
        <Metric label="Consultas" value={integer.format(report.totals.requests)} compact />
        <Metric
          label="Tokens"
          value={integer.format(report.totals.inputTokens + report.totals.outputTokens)}
          compact
        />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[720px] w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Usuario</th>
              <th className="px-4 py-3">Modelo</th>
              <th className="px-4 py-3 text-right">Consultas</th>
              <th className="px-4 py-3 text-right">Tokens</th>
              <th className="px-5 py-3 text-right">Costo</th>
            </tr>
          </thead>
          <tbody>
            {report.users.map((user) => (
              <tr key={`${user.userId}:${user.model}`} className="border-t border-slate-100">
                <td className="max-w-64 px-5 py-3">
                  <p
                    className="truncate font-medium text-slate-800"
                    title={user.email ?? undefined}
                  >
                    {user.email ?? 'Cuenta eliminada'}
                  </p>
                  <p
                    className="mt-0.5 truncate font-mono text-xs text-slate-500"
                    title={user.userId}
                  >
                    {user.userId}
                  </p>
                </td>
                <td className="px-4 py-3 text-slate-600">{user.model ?? '—'}</td>
                <td className="px-4 py-3 text-right">{integer.format(user.requests)}</td>
                <td className="px-4 py-3 text-right">
                  {integer.format(user.inputTokens + user.outputTokens)}
                </td>
                <td className="px-5 py-3 text-right font-semibold">{money.format(user.costUsd)}</td>
              </tr>
            ))}
            {report.users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  Aún no hay operaciones de IA en este periodo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function AdminOverviewSkeleton() {
  return (
    <SkeletonState label="Cargando administración…" className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="h-32" />
        ))}
      </div>
      <Skeleton className="h-80 w-full" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="h-28" />
        ))}
      </div>
    </SkeletonState>
  );
}

export function AdminUsageSkeleton() {
  return (
    <SkeletonState label="Cargando detalle de costo de IA…" className="space-y-3">
      <Skeleton className="h-16 w-full" />
      <div className="grid gap-3 sm:grid-cols-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Skeleton className="h-64 w-full" />
    </SkeletonState>
  );
}

function PeriodButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`tap-target rounded-lg px-3 text-sm font-semibold ${active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
    >
      {children}
    </button>
  );
}
function Metric({
  label,
  value,
  detail,
  compact = false,
}: {
  label: string;
  value: string;
  detail?: string;
  compact?: boolean;
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white ${compact ? 'p-4' : 'p-5'}`}>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-bold tracking-tight tabular-nums text-slate-900">{value}</p>
      {detail && <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>}
    </div>
  );
}
function RestrictedNotice() {
  return (
    <section className="max-w-xl rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="text-xl font-bold text-slate-900">Administración restringida</h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Tu cuenta no tiene el rol de administrador. La API protege estas métricas aunque se conozca
        esta URL.
      </p>
    </section>
  );
}
function dateFromKey(value: string) {
  return new Date(`${value}T12:00:00.000Z`);
}
function shortDate(value: string) {
  return new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' }).format(
    dateFromKey(value),
  );
}
function fullDate(value: string) {
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(dateFromKey(value));
}
function csvCell(value: string | number) {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}
